import React from 'react';
import {
    Chart as ChartJS,
    LineElement,
    PointElement,
    LinearScale,
    Title,
    Tooltip,
    Legend,
    CategoryScale
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
    LineElement,
    PointElement,
    LinearScale,
    Title,
    Tooltip,
    Legend,
    CategoryScale
);

interface LineChartProps {
    data: number[];
    labels: string[];
    title: string;
    color?: string;
    isDuration?: boolean;
}

const LineChart: React.FC<LineChartProps> = ({ data, labels, title, color = '#19CF7A', isDuration = false }) => {
    const chartData = {
        labels,
        datasets: [
            {
                label: title,
                data,
                borderColor: color,
                backgroundColor: (context: any) => {
                    const ctx = context.chart.ctx;
                    const gradient = ctx.createLinearGradient(0, 0, 0, 300);
                    gradient.addColorStop(0, `${color}40`);
                    gradient.addColorStop(1, `${color}00`);
                    return gradient;
                },
                borderWidth: 3,
                pointBackgroundColor: color,
                pointBorderColor: '#fff',
                pointBorderWidth: 2,
                pointRadius: 4,
                pointHoverRadius: 6,
                fill: true,
                tension: 0.4,
            },
        ],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                titleColor: '#1e293b',
                bodyColor: '#1e293b',
                borderColor: '#e2e8f0',
                borderWidth: 1,
                padding: 12,
                boxPadding: 4,
                usePointStyle: true,
                callbacks: {
                    label: (context: any) => {
                        const val = context.parsed.y;
                        if (isDuration) {
                            const m = Math.floor(val / 60);
                            const s = Math.floor(val % 60);
                            return m > 0 ? ` ${m}분 ${s}초` : ` ${s}초`;
                        }
                        return ` ${val.toLocaleString()}`;
                    },
                },
            },
        },
        scales: {
            x: {
                grid: {
                    display: false,
                },
                ticks: {
                    font: {
                        size: 11,
                    },
                    color: '#64748b',
                },
            },
            y: {
                beginAtZero: true,
                border: {
                    dash: [5, 5],
                },
                grid: {
                    color: '#e2e8f0',
                },
                ticks: {
                    font: {
                        size: 11,
                    },
                    color: '#64748b',
                    padding: 10,
                    callback: (value: any) => {
                        if (isDuration) {
                            const m = Math.floor(value / 60);
                            const s = Math.floor(value % 60);
                            return m > 0 ? `${m}분 ${s}초` : `${s}초`;
                        }
                        return value;
                    }
                },
            },
        },
    };

    return (
        <div style={{ position: 'relative', height: '300px', width: '100%' }}>
            <Line data={chartData} options={options} />
        </div>
    );
};

export default LineChart;
